/// <reference lib="dom" />
/** General settings section — favicon, platform adapter, breakpoints, and other site-wide config. */

import { html, render as litRender, nothing } from "lit-html";
import { projectState } from "../store";
import { updateSiteConfig } from "../site-context";
import { getPlatform } from "../platform";
import { openFileInTab } from "../files/files";

import type { ProjectConfig } from "@jxsuite/schema/types";

/** @param {HTMLElement} container */
export function renderGeneralSettings(container: HTMLElement) {
  const config = (projectState?.projectConfig || {}) as ProjectConfig;

  const onFaviconUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.ico,.svg";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      const platform = getPlatform();
      await platform.uploadFile("public/favicon.ico", file);
      await updateSiteConfig({ favicon: "/favicon.ico" });
      renderGeneralSettings(container);
    });
    input.click();
  };

  const onAdapterChange = (e: Event) => {
    void updateSiteConfig({
      build: { ...config.build, adapter: (e.target as HTMLInputElement).value },
    });
  };

  const onEditGlobalStyles = async () => {
    // Lazy import breaks the general-settings ↔ settings-modal module cycle
    const { closeSettingsModal } = await import("./settings-modal");
    closeSettingsModal();
    void openFileInTab("project.json");
  };

  // ─── Breakpoints ($media) ───────────────────────────────────────────────────

  const media = (config.$media || {}) as Record<string, string>;
  const mediaEntries = Object.entries(media);

  const onMediaValueChange = (key: string) => (e: Event) => {
    const updated = { ...media, [key]: (e.target as HTMLInputElement).value };
    void updateSiteConfig({ $media: updated });
  };

  const onMediaNameChange = (oldKey: string) => (e: Event) => {
    const rawName = (e.target as HTMLInputElement).value.trim();
    const newKey = rawName.startsWith("--") ? rawName : `--${rawName}`;
    if (newKey === oldKey) {
      return;
    }
    const updated = {} as Record<string, string>;
    for (const [k, v] of Object.entries(media)) {
      updated[k === oldKey ? newKey : k] = v;
    }
    void updateSiteConfig({ $media: updated });
    renderGeneralSettings(container);
  };

  const onRemoveBreakpoint = (key: string) => () => {
    const updated = { ...media };
    delete updated[key];
    void updateSiteConfig({ $media: updated });
    renderGeneralSettings(container);
  };

  const onAddBreakpoint = () => {
    const updated = { ...media, "--new": "(max-width: 480px)" };
    void updateSiteConfig({ $media: updated });
    renderGeneralSettings(container);
  };

  const currentFavicon = config.favicon;

  const tpl = html`
    <div class="settings-section">
      <h3 class="settings-section-title">General</h3>

      <div class="settings-field">
        <label class="settings-field-label">Favicon</label>
        <p class="settings-field-desc">Upload an image to use as the site favicon.</p>
        <div style="display:flex;align-items:center;gap:12px">
          ${currentFavicon
            ? html`<img
                src=${currentFavicon}
                alt="Current favicon"
                style="width:32px;height:32px;object-fit:contain;border:1px solid var(--border);border-radius:var(--radius);padding:2px"
              />`
            : html`<div
                style="width:32px;height:32px;border:1px dashed var(--border);border-radius:var(--radius);display:flex;align-items:center;justify-content:center;color:var(--fg-dim);font-size:var(--spectrum-font-size-50, 11px)"
              >
                —
              </div>`}
          <sp-action-button size="s" @click=${onFaviconUpload}> Upload Favicon </sp-action-button>
          ${currentFavicon
            ? html`<span style="font-size:var(--spectrum-font-size-50, 11px);color:var(--fg-dim)"
                >${currentFavicon}</span
              >`
            : nothing}
        </div>
      </div>

      <div class="settings-field">
        <label class="settings-field-label">Platform Adapter</label>
        <p class="settings-field-desc">Build adapter for deployment target.</p>
        <sp-picker
          size="s"
          label="Platform Adapter"
          .value=${config.build?.adapter || "static"}
          @change=${onAdapterChange}
        >
          <sp-menu-item value="static">Static</sp-menu-item>
          <sp-menu-item value="bun">Bun</sp-menu-item>
          <sp-menu-item value="node">Node</sp-menu-item>
          <sp-menu-item value="cloudflare-workers">Cloudflare Workers</sp-menu-item>
          <sp-menu-item value="cloudflare-pages">Cloudflare Pages</sp-menu-item>
        </sp-picker>
      </div>

      <div class="settings-field">
        <label class="settings-field-label">Breakpoints</label>
        <p class="settings-field-desc">
          Responsive breakpoints for canvas panels and media query styles.
        </p>
        <div class="settings-media-list">
          ${mediaEntries.map(([key, value]) => {
            const isBase = key === "--";
            return html`
              <div class="settings-media-row">
                ${isBase
                  ? html`<span class="settings-media-name-fixed">Base</span>`
                  : html`<sp-textfield
                      size="s"
                      class="settings-media-name"
                      .value=${key.replace(/^--/, "")}
                      placeholder="name"
                      @change=${onMediaNameChange(key)}
                    ></sp-textfield>`}
                <sp-textfield
                  size="s"
                  class="settings-media-value"
                  .value=${value}
                  placeholder=${isBase ? "1280px" : "(max-width: 768px)"}
                  @change=${onMediaValueChange(key)}
                ></sp-textfield>
                ${isBase
                  ? nothing
                  : html`<sp-action-button
                      size="s"
                      quiet
                      title="Remove breakpoint"
                      @click=${onRemoveBreakpoint(key)}
                    >
                      ×
                    </sp-action-button>`}
              </div>
            `;
          })}
        </div>
        <sp-action-button size="s" style="margin-top:8px" @click=${onAddBreakpoint}>
          + Add Breakpoint
        </sp-action-button>
      </div>

      <div class="settings-field">
        <label class="settings-field-label">Global Styles</label>
        <p class="settings-field-desc">Edit default element styles that apply across all pages.</p>
        <sp-action-button size="s" @click=${onEditGlobalStyles}>
          Edit Global Styles
        </sp-action-button>
      </div>
    </div>
  `;

  litRender(tpl, container);
}
